import React, { ChangeEvent, useEffect, useState } from 'react';

import BookmarksIcon from '@mui/icons-material/Bookmarks';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid';

interface Props {
  templates: Array<App.OfferTemplate>;
  templateType: string;
  propertyId?: string;
  roomTypeId?: string;
  roomRateId?: string;
  show: boolean;
  idOffer?: string;
  dealId?: string;
  hide: () => void;
  loadTemplate: (template: App.OfferTemplate) => void;
}

const emptyTemplate = {
  content: '',
  created_at: '',
  description: '',
  id: -1,
  offer_type: '',
  status: 'approved',
  template_type: 'fine_print',
  title: '',
  updated_at: '',
};

export default function OfferTemplateModal({
  templateType,
  templates: availableTemplates,
  show,
  dealId,
  propertyId,
  roomTypeId,
  roomRateId,
  hide,
  loadTemplate,
}: Props) {
  const [selectedTemplate, setSelectedTemplate] = useState<App.OfferTemplate>(emptyTemplate);
  const [templates, setTemplates] = useState(availableTemplates);

  useEffect(() => {
    if (!templateType) {
      setTemplates(templates);
      return;
    }

    const filteredTemplates = availableTemplates.filter((template) => template.template_type === templateType);
    setTemplates(filteredTemplates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateType, availableTemplates]);

  function onChangeSelectedTemplate(e: ChangeEvent<HTMLInputElement>) {
    const title = e.target.value;

    if (!title) {
      setSelectedTemplate(emptyTemplate);
      return;
    }

    const selectedTemplate = templates.find((template) => template.title === title);
    setSelectedTemplate(selectedTemplate);
  }

  function convertFinePrint(template_type: string): string {
    return template_type === 'fine_print'
      ? 'Fine print'
      : template_type === 'room_policies'
      ? 'Room Policies'
      : 'Location';
  }

  return (
    <Dialog open={show} onClose={hide}>
      <DialogTitle>Please select template</DialogTitle>
      <DialogContent>
        <Grid mt={1} size={{ sm: 6 }}>
          <FormControl fullWidth>
            <TextField
              value={convertFinePrint(templateType)}
              disabled
              name="Template Type"
              id="template-type"
              label="Template Type"
              InputLabelProps={{
                shrink: !!templateType,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BookmarksIcon color="secondary" />
                  </InputAdornment>
                ),
              }}
              color="warning"
              fullWidth
              variant="standard"
            />
          </FormControl>
        </Grid>
        <Grid mt={2} size={{ sm: 6 }}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Template Title</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={selectedTemplate?.title}
              label="Template Title"
              onChange={onChangeSelectedTemplate}
            >
              {templates.length === 0 && <MenuItem value="">None found</MenuItem>}
              {templates.length !== 0 && <MenuItem disabled value=""></MenuItem>}
              {templates.map((template) => (
                <MenuItem key={template.id} value={template.title}>
                  {template.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={hide} variant="contained" color="secondary">
          Close
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => loadTemplate(selectedTemplate)}
          disabled={templates.length === 0 || selectedTemplate.id === -1}
        >
          Import Template
        </Button>
      </DialogActions>
    </Dialog>
  );
}
